import { createAction } from "@reduxjs/toolkit";
import type {
    CalculationType,
    FinanceConfigType,
    FinanceInputsType,
} from "../../../../shared-logic/types/FinanceTypes";
import type { ParsedMonthlyRateType } from "../../../../../common-deprecated/types/FinanceInsuranceRateTypes";
import type { FetchAndParseJsonType } from "../../../../../common-deprecated/types/CommonTypes";
import type { FinanceUpdateTrigger } from "../../../../../common-deprecated/features/tradein/utils/tradeInAemUtilTypes";

export type FinanceConfigurationData = {
    config: FinanceConfigType | null;
    calculation: CalculationType | null;
};

export type BusinessFinanceStateType = {
    initialised: boolean;
    loading: boolean;
    error: boolean;
    input: FinanceInputsType;
} & FinanceConfigurationData;

export type LeasingFinanceResponseType = {
    config: FinanceConfigType;
    finance: { calculation: CalculationType };
};

export type StartFinanceRateFetchType = {
    trigger: FinanceUpdateTrigger;
    fetchAndParseJson?: FetchAndParseJsonType;
};

export type FinishFinanceRateFetchType = {
    result: ParsedMonthlyRateType;
    rates?: { [index: string]: string };
    trigger: FinanceUpdateTrigger;
    calculation?: CalculationType;
    previousDownPayment?: number;
};

export const initialBusinessFinanceState = {
    initialised: false,
    loading: false,
    error: false,
    input: {},
    calculation: null,
    config: null,
};

export const BUSINESS_FINANCE_INIT = "businessFinance/init";
export const BUSINESS_FINANCE_SET_INPUT = "businessFinance/setInput";
export const BUSINESS_FINANCE_START_CALCULATION = "businessFinance/startCalculation";
export const BUSINESS_FINANCE_FINISH_CALCULATION = "businessFinance/finishCalculation";
export const BUSINESS_FINANCE_ERROR_CALCULATION = "businessFinance/errorCalculation";
export const BUSINESS_START_FINANCE_RATE_FETCH = "businessFinance/startFinanceRateFetch";
export const BUSINESS_FINISH_FINANCE_RATE_FETCH = "businessFinance/finishFinanceRateFetch";
export const BUSINESS_ERROR_FINANCE_RATE_FETCH = "businessFinance/errorFinanceRateFetch";
export const BUSINESS_CLEAR_FINANCE_RATE = "businessFinance/clearFinanceRate";

export const businessSetFinanceInput = createAction<FinanceInputsType, typeof BUSINESS_FINANCE_SET_INPUT>(
    BUSINESS_FINANCE_SET_INPUT,
);

export const businessInitFinance = createAction<FinanceConfigurationData, typeof BUSINESS_FINANCE_INIT>(
    BUSINESS_FINANCE_INIT,
);

export const businessStartFinanceCalculation = createAction(BUSINESS_FINANCE_START_CALCULATION);

export const businessFinishFinanceCalculation = createAction<
    LeasingFinanceResponseType,
    typeof BUSINESS_FINANCE_FINISH_CALCULATION
>(BUSINESS_FINANCE_FINISH_CALCULATION);

export const businessStartFinanceRateFetch = createAction<
    StartFinanceRateFetchType,
    typeof BUSINESS_START_FINANCE_RATE_FETCH
>(BUSINESS_START_FINANCE_RATE_FETCH);

export const businessFinishFinanceRateFetch = createAction<
    FinishFinanceRateFetchType,
    typeof BUSINESS_FINISH_FINANCE_RATE_FETCH
>(BUSINESS_FINISH_FINANCE_RATE_FETCH);

export const businessErrorFinanceRateFetch = createAction(BUSINESS_ERROR_FINANCE_RATE_FETCH);
export const businessClearFinanceRate = createAction(BUSINESS_CLEAR_FINANCE_RATE);

export const businessLeasingErrorFinanceCalculation = createAction(BUSINESS_FINANCE_ERROR_CALCULATION);

export type BusinessLeasingActionsType =
    | ReturnType<typeof businessSetFinanceInput>
    | ReturnType<typeof businessInitFinance>
    | ReturnType<typeof businessStartFinanceCalculation>
    | ReturnType<typeof businessFinishFinanceCalculation>
    | ReturnType<typeof businessLeasingErrorFinanceCalculation>
    | ReturnType<typeof businessStartFinanceRateFetch>
    | ReturnType<typeof businessFinishFinanceRateFetch>
    | ReturnType<typeof businessErrorFinanceRateFetch>
    | ReturnType<typeof businessClearFinanceRate>;
